/*
 * seed.js — default site content (SPEC §7 / §9), Moab brand.
 *
 * Single source of truth for the public site until Firestore is wired up (Stage 5).
 * At that point this same data becomes the offline fallback (SPEC §10): pages read
 * Firestore first and fall back here so the site always renders 1:1.
 *
 * Image paths point at the bundled placeholders in /public/assets (SPEC §18).
 */

export const navigation = {
  // Home/About/Services/Contact experience — 5 items (SPEC §5)
  mainMenu: [
    { label: 'ABOUT', href: '/#about', order: 1 },
    { label: 'STRUCTURAL ENGINEERING', href: '/structural', order: 2 },
    { label: 'INFRASTRUCTURE ENGINEERING', href: '/infrastructure', order: 3 },
    { label: 'RESEARCH ENGINEERING', href: '/research', order: 4 },
    { label: 'CONTACT', href: '/#contact', order: 5 },
  ],
  // Structural/Infrastructure/Research detail pages — 4 items ("HOME" replaces "ABOUT", no "CONTACT")
  pageMenu: [
    { label: 'HOME', href: '/', order: 1 },
    { label: 'STRUCTURAL ENGINEERING', href: '/structural', order: 2 },
    { label: 'INFRASTRUCTURE ENGINEERING', href: '/infrastructure', order: 3 },
    { label: 'RESEARCH ENGINEERING', href: '/research', order: 4 },
  ],
};

export const footer = {
  brandName: 'Moab Planning & Civil Engineering',
  blurb: 'Leading civil engineering solutions with 10+ years of industry experience.',
  address: 'Taybe Industrial Zone, Taybe 4040000, Israel',
  quickLinksTitle: 'Quick Links',
  quickLinks: [
    { label: 'Home', href: '/', order: 1 },
    { label: 'About Us', href: '/#about', order: 2 },
    { label: 'Services', href: '/#services', order: 3 },
    { label: 'Contact Us', href: '/#contact', order: 4 },
  ],
  servicesTitle: 'Our Services',
  serviceLinks: [
    { label: 'Structural Engineering', href: '/structural', order: 1 },
    { label: 'Infrastructure Engineering', href: '/infrastructure', order: 2 },
    { label: 'Research Engineering', href: '/research', order: 3 },
  ],
  connectTitle: 'Connect With Us',
};

export const contactInfo = {
  // Footer (SPEC §6)
  phone: '00972-97995525',
  email: 'info@moab-eng.com',
  addressLine: 'Taybe Industrial Zone, Taybe 4040000, Israel',
  fax: '00972-97995526',
  // Contact Us section form side (SPEC §7.1.4)
  formPhone: '00972-97995525',
  formEmail: 'info@moab-eng.com',
  formAddress: 'Taybe Industrial Zone, Taybe 4040000, Israel',
  // Interactive map: a Google Maps embed URL (admin-editable). Defaults to the
  // office coordinates with a zoom-in. Leave blank to build one from the address.
  mapEmbed: 'https://maps.google.com/maps?q=32.246854,34.994033&z=16&output=embed',
};

// Promo popups (created in the admin; empty by default so nothing shows until set).
export const popups = [];

// Our Clients — infinite marquee (logo optional; name shown until a logo is set).
export const clients = [
  { id: 'strauss', name: 'Strauss Group', logo: '', order: 1 },
  { id: 'energix', name: 'Energix Renewable Energies', logo: '', order: 2 },
  { id: 'tlv', name: 'Tel Aviv-Yafo Municipality', logo: '', order: 3 },
  { id: 'kfar-saba', name: 'Kfar Saba Municipality', logo: '', order: 4 },
  { id: 'lod-edc', name: 'Lod Economic Development Corporation', logo: '', order: 5 },
  { id: 'rishon-ec', name: 'Rishon LeZion Economic Corporation', logo: '', order: 6 },
  { id: 'enlight', name: 'Enlight Renewable Energy', logo: '', order: 7 },
  { id: 'nextcom', name: 'Nextcom Group', logo: '', order: 8 },
  { id: 'karnei-shomron', name: 'Karnei Shomron Local Council', logo: '', order: 9 },
  { id: 'shivet-achim', name: 'Shivet Achim Ltd.', logo: '', order: 10 },
  { id: 'emek-hefer', name: 'Emek Hefer Regional Council', logo: '', order: 11 },
  { id: 'massar', name: 'Massar International', logo: '', order: 12 },
  { id: 'shikma-besor', name: 'Shikma Besor Drainage Authority', logo: '', order: 13 },
  { id: 'afcon', name: 'Afcon Holdings', logo: '', order: 14 },
  { id: 'kochav-yair', name: 'Kochav Yair-Tzur Yigal Local Council', logo: '', order: 15 },
];

export const socials = {
  instagram: '#',
  facebook: '#',
  linkedin: '#',
};

export const about = {
  eyebrow: 'About Us',
  title: 'We Are The Leader In Civil engineering',
  paragraph:
    "Lorem Ipsum is simply dummy text of the printing and typesetting industry. " +
    "Lorem Ipsum has been the industry's standard dummy text ever since the 1500s.",
  image: '/assets/images/about-engineer.svg',
  checklist: [
    '100% Project Success Rate',
    'No Hidden Costs',
    'Licensed Engineering Team',
    'Industry-Leading Standards',
    '24/7 Site Support',
    'Advanced Structural Analysis',
    'Free Initial Consultation',
    'Timely Project Completion',
  ],
};

// Editable on-page text for the home section headings + intros (SPEC §7.1.3/§7.1.4).
export const homeSections = {
  servicesTitleWhite: 'Our',
  servicesTitleAccent: 'Services',
  servicesIntro:
    'We provide three different services within the field of civil engineering, each ' +
    'offering specialized solutions. Research, Structural and Infrastructure engineering.',
  contactTitle: 'Contact Us',
  contactBlurb: "For all your civil engineering needs, we're here to help. Don't hesitate to contact us!",
};

// Editable detail-page hero text (title is two-tone: accent word first, then white).
const DETAIL_LEAD =
  'We provide specialized solutions in structural engineering, combining innovative design ' +
  'with practical implementation for buildings, bridges, and infrastructure projects.';

export const pageStructural = { titleAccent: 'Structural', titleWhite: 'Engineering', lead: DETAIL_LEAD };
export const pageInfrastructure = { titleAccent: 'Infrastructure', titleWhite: 'Engineering', lead: DETAIL_LEAD };
export const pageResearch = { titleAccent: 'Research', titleWhite: 'Engineering', lead: DETAIL_LEAD };

export const services = [
  {
    id: 'research',
    icon: 'search',
    title: 'Engineering Research',
    body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
    ctaLabel: 'Explore',
    ctaHref: '/research',
    order: 1,
  },
  {
    id: 'structural',
    icon: 'building',
    title: 'Structural Engineering',
    body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
    ctaLabel: 'Explore',
    ctaHref: '/structural',
    order: 2,
  },
  {
    id: 'infrastructure',
    icon: 'ruler',
    title: 'Infrastructure Engineering',
    body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
    ctaLabel: 'Explore',
    ctaHref: '/infrastructure',
    order: 3,
  },
];

const heroBody =
  'Civil engineering research encompasses a broad range of areas, from infrastructure ' +
  'development and material science to environmental concerns and smart city solutions';

export const homeHero = [
  { id: 'slide-1', image: '/assets/images/hero-blueprint.svg', bodyText: heroBody, ctaLabel: 'Explore', ctaHref: '/research', order: 1 },
  { id: 'slide-2', image: '/assets/images/hero-blueprint.svg', bodyText: heroBody, ctaLabel: 'Explore', ctaHref: '/structural', order: 2 },
  { id: 'slide-3', image: '/assets/images/hero-blueprint.svg', bodyText: heroBody, ctaLabel: 'Explore', ctaHref: '/infrastructure', order: 3 },
];

export const structuralCards = [
  {
    id: 'steel',
    title: 'Steel Structure Design',
    body:
      'Modern steel structure solutions for industrial and commercial buildings. We combine ' +
      'strength with aesthetic appeal in all our steel designs.',
    image: '/assets/images/structural-steel.svg',
    readMoreLabel: 'Read more',
    readMoreHref: '#',
    order: 1,
  },
  {
    id: 'concrete',
    title: 'Concrete Technology',
    body:
      'Advanced concrete mixtures and applications for durable structures. Our research in ' +
      'concrete technology leads to longer-lasting, more sustainable buildings.',
    image: '/assets/images/structural-concrete.svg',
    readMoreLabel: 'Read more',
    readMoreHref: '#',
    order: 2,
  },
  {
    id: 'analysis',
    title: 'Structural Analysis',
    body:
      'Advanced structural analysis for high-rise buildings using cutting-edge technology. Our ' +
      'team specializes in complex load calculations and innovative solutions for modern architecture.',
    image: '/assets/images/structural-analysis.svg',
    readMoreLabel: 'Read more',
    readMoreHref: '#',
    order: 3,
  },
  {
    id: 'sustainable',
    title: 'Sustainable Design',
    body:
      'Environmentally responsible structural design that reduces material waste and embodied ' +
      'carbon while maintaining performance and resilience.',
    image: '/assets/images/structural-steel.svg',
    readMoreLabel: 'Read more',
    readMoreHref: '#',
    order: 4,
  },
];

export const infrastructureProjects = [
  {
    id: 'project-1',
    date: '26 June 2020',
    title: 'Infrastructure Project 1',
    body:
      'Advanced infrastructure solutions for modern cities. Our team specializes in complex ' +
      'urban planning and innovative solutions for growing metropolitan areas.',
    image: '/assets/images/infra-project-1.svg',
    readMoreLabel: 'READ MORE',
    readMoreHref: '#',
    order: 1,
  },
  {
    id: 'project-2',
    date: '14 March 2021',
    title: 'Infrastructure Project 2',
    body:
      'Resilient transport networks engineered for capacity and longevity. We deliver roads, ' +
      'bridges and interchanges built to modern standards.',
    image: '/assets/images/infra-project-1.svg',
    readMoreLabel: 'READ MORE',
    readMoreHref: '#',
    order: 2,
  },
  {
    id: 'project-3',
    date: '02 September 2022',
    title: 'Infrastructure Project 3',
    body:
      'Smart utility and drainage systems that keep cities running. Our designs integrate ' +
      'monitoring and sustainability from day one.',
    image: '/assets/images/infra-project-1.svg',
    readMoreLabel: 'READ MORE',
    readMoreHref: '#',
    order: 3,
  },
];

export const researchItems = [
  {
    id: 'materials',
    title: 'Innovative Materials for Sustainable Construction',
    body:
      'Exploring advanced and eco-friendly construction materials to improve structural ' +
      'performance and reduce environmental impact in modern infrastructure projects.',
    image: '/assets/images/research-materials.svg',
    ctaLabel: 'Read the study',
    ctaHref: '#',
    order: 1,
  },
  {
    id: 'seismic',
    title: 'Seismic-Resistant Design and Earthquake Engineering',
    body:
      'Research focused on improving the resilience of structures against seismic activities ' +
      'using advanced modeling and experimental validation techniques.',
    image: '/assets/images/research-seismic.svg',
    ctaLabel: 'Read the study',
    ctaHref: '#',
    order: 2,
  },
  {
    id: 'smart-infra',
    title: 'Smart Infrastructure and Monitoring Systems',
    body:
      'Developing sensor-based monitoring systems to assess real-time structural health and ' +
      'improve long-term performance of bridges, tunnels, and buildings.',
    image: '/assets/images/research-smart-infra.svg',
    ctaLabel: 'Read the study',
    ctaHref: '#',
    order: 3,
  },
  {
    id: 'drainage',
    title: 'Sustainable Urban Drainage and Water Management',
    body:
      'Investigating innovative solutions for stormwater management, flood mitigation, and ' +
      'sustainable urban drainage systems in rapidly growing cities.',
    image: '/assets/images/research-drainage.svg',
    ctaLabel: 'Read the study',
    ctaHref: '#',
    order: 4,
  },
];

export const ctaStructural = {
  title: 'Ready to start your project?',
  subtitle: 'Our team of structural engineers is ready to help with your next construction project.',
  buttonLabel: 'CONTACT US',
  buttonHref: '/#contact',
};

export const ctaInfrastructure = {
  title: 'Ready to start your project?',
  subtitle: 'Our team of structural engineers is ready to help with your next construction project.',
  buttonLabel: 'CONTACT US',
  buttonHref: '/#contact',
};

export const ctaResearch = {
  title: 'Are you interested in our researches?',
  subtitle:
    'Our team of Research in civil engineering is ready to help and provide you with more informations.',
  buttonLabel: 'CONTACT US',
  buttonHref: '/#contact',
};

const seed = {
  navigation,
  footer,
  contactInfo,
  socials,
  about,
  homeSections,
  pageStructural,
  pageInfrastructure,
  pageResearch,
  services,
  homeHero,
  structuralCards,
  infrastructureProjects,
  researchItems,
  clients,
  ctaStructural,
  ctaInfrastructure,
  ctaResearch,
};

export default seed;
